import { useQuery } from '@tanstack/react-query';
import axios from 'src/axios';

export const GEO_REFERENCE_QUERY_KEY = ['geo', 'reference'];

const EMPTY_REFERENCE = { countries: [], subdivisions: {} };

async function fetchGeoReference() {
  const { data } = await axios.get('/geo/reference');
  return data;
}

export function useGeoReference() {
  const query = useQuery({
    queryKey: GEO_REFERENCE_QUERY_KEY,
    queryFn: fetchGeoReference,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1
  });

  return {
    reference: query.data || EMPTY_REFERENCE,
    isLoading: query.isLoading,
    isError: query.isError
  };
}

export function countryOptions(reference) {
  return (reference?.countries || []).map((c) => ({
    value: c.code,
    label: c.name,
    region: c.region
  }));
}

export function findCountry(reference, countryCode) {
  if (!countryCode) return null;
  const code = String(countryCode).toUpperCase();
  return (reference?.countries || []).find((c) => c.code === code) || null;
}

export function subdivisionOptions(reference, countryCode) {
  if (!countryCode) return null;
  const list = reference?.subdivisions?.[String(countryCode).toUpperCase()];
  if (!list) return null;
  return list.map((s) => ({ value: s.code, label: s.name }));
}

export function subdivisionLabel(reference, countryCode) {
  return (
    findCountry(reference, countryCode)?.subdivisionLabel ||
    'State / Province / Region'
  );
}

export function subdivisionName(reference, countryCode, subdivisionCode) {
  if (!subdivisionCode) return '';
  const list = reference?.subdivisions?.[String(countryCode).toUpperCase()];
  const match = (list || []).find((s) => s.code === subdivisionCode);
  return match ? match.name : subdivisionCode;
}

export function normalisePostcode(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

export function validatePostcode(reference, countryCode, value) {
  const country = findCountry(reference, countryCode);
  const countryRule = country?.postcode || null;
  const normalised = normalisePostcode(value);

  if (!normalised || !countryCode) {
    return {
      valid: true,
      validated: false,
      generic: false,
      expectedFormat: countryRule?.format || null,
      example: countryRule?.example || null
    };
  }

  if (country && country.hasPostcode === false) {
    return {
      valid: true,
      validated: false,
      generic: false,
      expectedFormat: null,
      example: null
    };
  }

  const rule = countryRule || reference?.genericPostcode || null;
  if (!rule) {
    return {
      valid: true,
      validated: false,
      generic: false,
      expectedFormat: null,
      example: null
    };
  }

  const generic = !countryRule;

  let valid = true;
  try {
    valid = new RegExp(rule.pattern).test(normalised);
  } catch {
    return {
      valid: true,
      validated: false,
      generic: false,
      expectedFormat: rule.format || null,
      example: rule.example || null
    };
  }

  return {
    valid,
    validated: !generic,
    generic,
    expectedFormat: rule.format || null,
    example: rule.example || null
  };
}

export function countryHasPostcode(reference, countryCode) {
  const country = findCountry(reference, countryCode);
  return country ? country.hasPostcode !== false : true;
}
